import React, { useState, useEffect } from 'react';
import { Users, Plus, Search, Filter, Eye, Edit, UserCheck, UserX, ArrowUpDown, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { firebaseService } from '../services/firebaseService';
import AlunoModal from '../components/AlunoModal';
import { useNotification } from '../hooks/useNotification';

const Alunos: React.FC = () => {
  const { userProfile } = useAuth();
  const { showSuccess, showError, showConfirm, NotificationComponent } = useNotification();
  const [alunos, setAlunos] = useState<any[]>([]);
  const [niveis, setNiveis] = useState<any[]>([]);
  const [turmas, setTurmas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAluno, setSelectedAluno] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all', // all, active, inactive
    nivel: 'all',
    turma: 'all'
  });
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!userProfile?.empresaCnpj) return;
      
      try {
        setLoading(true);
        // Usar função otimizada que inclui alunos
        const { alunos: alunosData, turmas: turmasData } = await firebaseService.getAllDataWithAlunos(userProfile.empresaCnpj);
        const niveisData = await firebaseService.getNiveis(userProfile.empresaCnpj);
        
        setAlunos(alunosData);
        setNiveis(niveisData);
        setTurmas(turmasData);
        console.log('✅ Dados carregados:', {
          alunos: alunosData.length,
          niveis: niveisData.length,
          turmas: turmasData.length
        });
      } catch (err) {
        setError('Erro ao carregar dados');
        console.error('Erro ao carregar dados:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userProfile?.empresaCnpj]);

  // Filtrar e ordenar alunos
  const filteredAndSortedAlunos = alunos.filter(aluno => {
    // Verificação de segurança - se aluno não existe, pula
    if (!aluno) return false;
    
    // Filtro de busca por texto
    const matchesSearch = searchTerm === '' || 
      (aluno.nome || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (aluno.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (aluno.telefone || '').includes(searchTerm) ||
      (aluno.cellular || '').includes(searchTerm);

    // Filtro de status
    const matchesStatus = filters.status === 'all' || 
      (filters.status === 'active' && aluno.bAtivo) ||
      (filters.status === 'inactive' && !aluno.bAtivo);

    // Filtro de nível
    const matchesNivel = filters.nivel === 'all' || aluno.nivelId === filters.nivel;

    // Filtro de turma
    const matchesTurma = filters.turma === 'all' || 
      (aluno.turmasIds && aluno.turmasIds.includes(filters.turma));

    return matchesSearch && matchesStatus && matchesNivel && matchesTurma;
  }).sort((a, b) => {
    if (!sortConfig) return 0;
    
    const { key, direction } = sortConfig;
    let aValue = a[key];
    let bValue = b[key];
    
    // Tratamento especial para diferentes tipos de dados
    if (key === 'nome' || key === 'email' || key === 'nivel') {
      aValue = (aValue || '').toString().toLowerCase();
      bValue = (bValue || '').toString().toLowerCase();
    } else if (key === 'bAtivo') {
      aValue = aValue ? 1 : 0;
      bValue = bValue ? 1 : 0;
    }
    
    if (aValue < bValue) {
      return direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Calcular estatísticas com verificações de segurança
  const totalAlunos = alunos.length;
  const alunosAtivos = alunos.filter(a => a && a.bAtivo).length;
  const alunosInativos = totalAlunos - alunosAtivos;
  const novosEsteMes = alunos.filter(a => {
    if (!a || !a.createDate) return false;
    const dataCriacao = new Date(a.createDate);
    const agora = new Date();
    return dataCriacao.getMonth() === agora.getMonth() && 
           dataCriacao.getFullYear() === agora.getFullYear();
  }).length;

  // Funções do modal
  const handleViewAluno = (aluno: any) => {
    console.log('Abrindo modal para aluno:', aluno.nome);
    setSelectedAluno(aluno);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAluno(null);
  };

  const handleSaveAluno = async (alunoData: any) => {
    if (!userProfile?.empresaCnpj) return;

    try {
      if (alunoData.id) {
        // Atualizar aluno existente
        await firebaseService.updateAluno(userProfile.empresaCnpj, alunoData.id, alunoData);
        // Atualizar lista local
        setAlunos(prev => prev.map(aluno => 
          aluno.id === alunoData.id ? { ...aluno, ...alunoData } : aluno
        ));
      } else {
        // Criar novo aluno
        const alunoId = await firebaseService.createAluno(userProfile.empresaCnpj, alunoData);
        // Recarregar dados
        const { alunos: alunosData } = await firebaseService.getAllDataWithAlunos(userProfile.empresaCnpj);
        setAlunos(alunosData);
      }
      handleCloseModal();
      showSuccess(
        alunoData.id ? 'Aluno atualizado' : 'Aluno criado',
        alunoData.id ? 'Os dados do aluno foram atualizados com sucesso.' : 'O aluno foi cadastrado com sucesso.'
      );
      return Promise.resolve();
    } catch (error) {
      console.error('Erro ao salvar aluno:', error);
      showError('Erro ao salvar', 'Não foi possível salvar o aluno. Tente novamente.');
      throw error;
    }
  };

  // Funções de filtro
  const handleFilterChange = (filterType: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      nivel: 'all',
      turma: 'all'
    });
    setSearchTerm('');
  };

  // Função para ordenação
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Função para inativar aluno
  const handleInactivateAluno = async (aluno: any) => {
    if (!userProfile?.empresaCnpj) return;
    
    showConfirm(
      'Inativar aluno',
      `Tem certeza que deseja inativar o aluno ${aluno.nome}?`,
      async () => {
        try {
          await firebaseService.updateAlunoStatus(userProfile.empresaCnpj, aluno.id, false);
          setAlunos(prev => prev.map(a => 
            a.id === aluno.id ? { ...a, bAtivo: false } : a
          ));
          showSuccess('Aluno inativado', 'O aluno foi inativado com sucesso.');
        } catch (error) {
          console.error('❌ Erro ao inativar aluno:', error);
          showError('Erro ao inativar', 'Não foi possível inativar o aluno. Tente novamente.');
        }
      }
    );
  };

  // Função para ativar aluno
  const handleActivateAluno = async (aluno: any) => {
    if (!userProfile?.empresaCnpj) return;
    
    showConfirm(
      'Ativar aluno',
      `Tem certeza que deseja ativar o aluno ${aluno.nome}?`,
      async () => {
        try {
          await firebaseService.updateAlunoStatus(userProfile.empresaCnpj, aluno.id, true);
          setAlunos(prev => prev.map(a => 
            a.id === aluno.id ? { ...a, bAtivo: true } : a
          ));
          showSuccess('Aluno ativado', 'O aluno foi ativado com sucesso.');
        } catch (error) {
          console.error('❌ Erro ao ativar aluno:', error);
          showError('Erro ao ativar', 'Não foi possível ativar o aluno. Tente novamente.');
        }
      }
    );
  };

  // Função para deletar aluno
  const handleDeleteAluno = async (aluno: any) => {
    if (!userProfile?.empresaCnpj) return;
    
    showConfirm(
      'Deletar aluno',
      `Tem certeza que deseja deletar o aluno ${aluno.nome}?\n\nEsta ação não pode ser desfeita.`,
      async () => {
        try {
          await firebaseService.deleteAluno(userProfile.empresaCnpj, aluno.id);
          setAlunos(prev => prev.filter(a => a.id !== aluno.id));
          showSuccess('Aluno deletado', 'O aluno foi deletado com sucesso.');
        } catch (error) {
          console.error('❌ Erro ao deletar aluno:', error);
          showError('Erro ao deletar', 'Não foi possível deletar o aluno. Tente novamente.');
        }
      },
      {
        confirmText: 'Deletar',
        cancelText: 'Cancelar'
      }
    );
  };

  // Se ainda está carregando, mostra loading
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando dados dos alunos...</p>
        </div>
      </div>
    );
  }

  // Se há erro, mostra mensagem de erro
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <Users className="h-12 w-12 mx-auto mb-2" />
            <p className="text-lg font-medium">Erro ao carregar alunos</p>
            <p className="text-sm text-gray-600">{error}</p>
          </div>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <NotificationComponent />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <span className="text-gray-500">Alunos</span>
              </li>
              <li>
                <span className="text-gray-400 dark:text-gray-500">/</span>
              </li>
              <li>
                <span className="text-gray-900 font-medium">Todos</span>
              </li>
            </ol>
          </nav>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">Todos</h1>
        </div>
        <button 
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          onClick={() => {
            setSelectedAluno(null);
            setIsModalOpen(true);
          }}
        >
          <Plus className="h-4 w-4" />
          <span>Novo Aluno</span>
        </button>
      </div>

      {/* Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-sm text-gray-600">
          <span className="font-medium">{filteredAndSortedAlunos.length}</span> alunos encontrados de{' '}
          <span className="font-medium">{totalAlunos}</span> alunos cadastrados
          {alunosAtivos > 0 && (
            <> • <span className="font-medium text-green-600">{alunosAtivos}</span> ativos</>
          )}
          {alunosInativos > 0 && (
            <> • <span className="font-medium text-orange-600">{alunosInativos}</span> inativos</>
          )}
        </div>
      </div>

      {/* Search and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4" />
            <input
              type="text"
              placeholder="Procurar por nome, email ou telefone"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-80"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-2 border rounded-md transition-colors ${
              showFilters || filters.status !== 'all' || filters.nivel !== 'all' || filters.turma !== 'all'
                ? 'border-green-500 bg-green-50 text-green-700 hover:bg-green-100'
                : 'border-primary-300 text-primary-600 hover:bg-primary-50'
            }`}
          >
            <Filter className="h-4 w-4" />
            <span>Filtros</span>
            {(filters.status !== 'all' || filters.nivel !== 'all' || filters.turma !== 'all') && (
              <span className="ml-1 h-2 w-2 rounded-full bg-green-600"></span>
            )}
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="all">Todos</option>
                <option value="active">Ativos</option>
                <option value="inactive">Inativos</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nível
              </label>
              <select
                value={filters.nivel}
                onChange={(e) => handleFilterChange('nivel', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="all">Todos os níveis</option>
                {niveis.map(nivel => (
                  <option key={nivel.id} value={nivel.id}>
                    {nivel.nome}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Turma
              </label>
              <select
                value={filters.turma}
                onChange={(e) => handleFilterChange('turma', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="all">Todas as turmas</option>
                {turmas.map(turma => (
                  <option key={turma.id} value={turma.id}>
                    {turma.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm font-medium text-gray-700 transition-colors"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200" style={{ minWidth: '1200px' }}>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-80">
                  <button
                    onClick={() => handleSort('nome')}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    Aluno
                    {sortConfig?.key === 'nome' ? (
                      sortConfig.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 opacity-50" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">
                  Contato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  <button
                    onClick={() => handleSort('nivel')}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    Nível
                    {sortConfig?.key === 'nivel' ? (
                      sortConfig.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 opacity-50" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Turma
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  <button
                    onClick={() => handleSort('bAtivo')}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    Status
                    {sortConfig?.key === 'bAtivo' ? (
                      sortConfig.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 opacity-50" />
                    )}
                  </button>
                </th>
                <th className="relative px-6 py-3 w-32">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredAndSortedAlunos.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <Users className="h-12 w-12 text-gray-400 dark:text-gray-600 mb-3" />
                      <p className="text-gray-500 dark:text-gray-400 font-medium">Nenhum aluno encontrado</p>
                      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Tente ajustar os filtros de busca</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredAndSortedAlunos.map((aluno) => (
                  <tr key={aluno.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          {aluno.fotoUrl ? (
                            <img 
                              src={aluno.fotoUrl} 
                              alt={aluno.nome}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-primary-600">
                                {(aluno.nome || 'A').split(' ').map((n: string) => n[0]).join('')}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{aluno.nome || 'Nome não informado'}</div>
                          <div className="text-sm text-gray-500">{aluno.email || 'Email não informado'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{aluno.email || 'Email não informado'}</div>
                      <div className="text-sm text-gray-500">{aluno.telefone || aluno.cellular || 'Telefone não informado'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {aluno.nivel || 'Não definido'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(() => {
                        // Obter IDs das turmas do aluno
                        let turmasIds: string[] = [];
                        
                        // Verificar diferentes formatos possíveis
                        if (Array.isArray(aluno.turmas)) {
                          turmasIds = aluno.turmas;
                        } else if (typeof aluno.turmas === 'string') {
                          try {
                            turmasIds = JSON.parse(aluno.turmas);
                          } catch {
                            turmasIds = [aluno.turmas];
                          }
                        } else if (aluno.turmasIds && Array.isArray(aluno.turmasIds)) {
                          turmasIds = aluno.turmasIds;
                        }
                        
                        // Buscar nomes das turmas
                        if (turmasIds.length > 0) {
                          const turmasNomes = turmasIds
                            .map((turmaId: string) => {
                              const turma = turmas.find(t => t.id === turmaId);
                              return turma?.nome;
                            })
                            .filter(Boolean);
                          
                          if (turmasNomes.length > 0) {
                            return (
                              <div className="flex flex-wrap gap-1">
                                {turmasNomes.map((nome: string, index: number) => (
                                  <span 
                                    key={index}
                                    className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800"
                                  >
                                    {nome}
                                  </span>
                                ))}
                              </div>
                            );
                          }
                        }
                        
                        return <span className="text-gray-500">Sem turma</span>;
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        aluno.bAtivo 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {aluno.bAtivo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-x-1">
                        <button
                          onClick={() => handleViewAluno(aluno)}
                          className="text-primary-600 hover:text-primary-900 p-2 rounded hover:bg-primary-50 transition-colors"
                          title="Visualizar detalhes"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleViewAluno(aluno)}
                          className="text-gray-600 hover:text-gray-900 p-2 rounded hover:bg-gray-50 transition-colors"
                          title="Editar aluno"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => aluno.bAtivo ? handleInactivateAluno(aluno) : handleActivateAluno(aluno)}
                          className={`p-2 rounded transition-colors ${
                            aluno.bAtivo 
                              ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50' 
                              : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                          }`}
                          title={aluno.bAtivo ? 'Inativar aluno' : 'Ativar aluno'}
                        >
                          {aluno.bAtivo ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => handleDeleteAluno(aluno)}
                          className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50 transition-colors"
                          title="Deletar aluno"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <AlunoModal
        aluno={selectedAluno}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveAluno}
        niveis={niveis}
        turmas={turmas}
      />
    </div>
  );
};

export default Alunos;
